// SEC-1
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Box, Collapse, Stack } from "@mui/material";
import { AsyncContentMui } from "components";
import { ClientCharacteristicValues, NavBarHorizontal } from "components/Client";
import { ClientRoleGuard } from "components/guards";
import { useActionHook, useNavigate } from "hooks";
import { ClientRouter } from "Routers/Client-routes/ClientRouter";
import { useClientActions } from "store";

import { RoleName } from "../../constants";

export const Client: React.FC = () => {
  const { client_id } = useParams<{ client_id: string }>();
  const navigate = useNavigate();

  const clientId = client_id && !isNaN(Number(client_id)) ? Number(client_id) : null;

  const { getClientDetailAction } = useClientActions();
  const { loading: isFetching, action: fetchClientDetail } = useActionHook(getClientDetailAction);

  // Collapse/expand the whole characteristics bar (mirrors the deal's MoreHoriz toggle).
  const [isCharBarOpen, setIsCharBarOpen] = useState<boolean>(true);

  // Fetch the client detail on mount and whenever the id changes.
  useEffect(() => {
    if (clientId !== null && clientId > 0) {
      fetchClientDetail(clientId);
    }
  }, [clientId, fetchClientDetail]);

  // Redirect on invalid/missing client_id
  useEffect(() => {
    if (clientId === null) {
      navigate({ route: "clients" });
    }
  }, [clientId, navigate]);

  const isLoading = clientId === null || isFetching;

  return (
    <AsyncContentMui isLoading={isLoading} spinnerProps={{ size: 200 }}>
      <Stack
        alignItems="stretch"
        gap="16px"
        width="100%"
        height="100%"
        overflow="hidden"
        data-testid="client-page"
        className="client"
      >
        <Stack
          sx={{ position: "relative" }}
          direction="row"
          useFlexGap
          justifyContent="space-between"
          alignItems="center"
        >
          <ClientRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}>
            <NavBarHorizontal />
          </ClientRoleGuard>
          <MoreHorizIcon
            sx={{
              backgroundColor: "#ffffff",
              cursor: "pointer",
              width: "35px",
              height: "20px",
              borderRadius: "7px",
              border: "0.5px solid #dae0e2",
              position: "absolute",
              left: "-6px",
              // Center on the divider: outer Stack gap is 16px, so drop the icon by gap + half its height.
              bottom: "-26px",
              right: "0",
              marginLeft: "auto",
              marginRight: "auto",
              zIndex: "10"
            }}
            onClick={() => setIsCharBarOpen((prev) => !prev)}
          />
        </Stack>
        <ClientRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}>
          <Box sx={{ ...(!isCharBarOpen && { borderTop: "1px solid #ced6d8" }) }}>
            <Collapse in={isCharBarOpen} timeout={500} unmountOnExit>
              <ClientCharacteristicValues />
            </Collapse>
          </Box>
        </ClientRoleGuard>
        <Stack direction="row" flexGrow={1} position="relative" overflow="hidden">
          <Stack
            sx={{
              overflow: "hidden",
              position: "relative",
              width: "100%",
              borderRadius: "8px"
            }}
          >
            <ClientRouter />
          </Stack>
        </Stack>
      </Stack>
    </AsyncContentMui>
  );
};
